/**
 * AccessibilityContext — global state for student accessibility preferences.
 *
 * Five toggleable settings, all persisted to localStorage:
 *   - largeText           (CSS class `a11y-large-text` on <body>)
 *   - highContrast        (CSS class `a11y-high-contrast`)
 *   - reduceMotion        (CSS class `a11y-reduce-motion`)
 *   - readAloudEnabled    (whether Read-Aloud buttons are visible / auto-play)
 *   - speechInputEnabled  (whether mic buttons are visible)
 *
 * The settings panel writes into this context; all consuming components read.
 *
 * Initial state respects the user's OS-level `prefers-reduced-motion` media
 * query so we never ignore an existing system signal.
 */
import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";

const STORAGE_KEY = "lsbp.a11y.v1";

const DEFAULTS = {
  largeText: false,
  highContrast: false,
  reduceMotion: false,
  readAloudEnabled: true,
  speechInputEnabled: true,
};

const AccessibilityContext = createContext({
  settings: DEFAULTS,
  update: () => {},
  reset: () => {},
});

function readInitial() {
  if (typeof window === "undefined") return DEFAULTS;
  let stored = {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw) stored = JSON.parse(raw) || {};
  } catch {
    stored = {};
  }
  // Honor OS-level reduced-motion if user hasn't chosen a value yet
  let osReducedMotion = false;
  try {
    osReducedMotion = window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    osReducedMotion = false;
  }
  return {
    ...DEFAULTS,
    reduceMotion: osReducedMotion,
    ...stored,
  };
}

export function AccessibilityProvider({ children }) {
  const [settings, setSettings] = useState(readInitial);

  // Persist + apply CSS classes whenever settings change
  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch {
      /* private mode etc. */
    }
    const cls = document.body.classList;
    cls.toggle("a11y-large-text", settings.largeText);
    cls.toggle("a11y-high-contrast", settings.highContrast);
    cls.toggle("a11y-reduce-motion", settings.reduceMotion);
  }, [settings]);

  const update = useCallback((patch) => {
    setSettings((prev) => ({ ...prev, ...patch }));
  }, []);
  const reset = useCallback(() => setSettings(DEFAULTS), []);

  const value = useMemo(() => ({ settings, update, reset }), [settings, update, reset]);
  return (
    <AccessibilityContext.Provider value={value}>
      {children}
    </AccessibilityContext.Provider>
  );
}

export function useAccessibility() {
  return useContext(AccessibilityContext);
}
